// Local-calendar date helpers.
//
// Why this exists: `new Date('YYYY-MM-DD')` parses a date-only string as UTC midnight.
// In timezones behind UTC (e.g. America/Chicago) that instant is the PREVIOUS local day,
// so `new Date(date).toLocaleDateString()` renders one day earlier than the user picked.
// Likewise `new Date().toISOString().split('T')[0]` is UTC's "today", not the user's —
// an entry created in the evening gets dated TOMORROW. Both caused billing entries to
// display (and be stored) on the wrong day. Use these helpers everywhere a user-picked
// calendar date is defaulted, stored, or displayed.
//
// Storage contract: dates the user picks are sent/stored as plain 'YYYY-MM-DD' strings
// and are never rewritten by the backend, so fixing it on the client fixes it everywhere.

// Internal: date -> 'YYYY-MM-DD' in its local calendar.
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Local calendar "today" as YYYY-MM-DD (uses the browser's own timezone).
export const todayLocalISO = () => ymd(new Date());

// Local calendar date N days from `base` as YYYY-MM-DD (e.g. invoice due dates).
export const plusDaysLocalISO = (n, base = new Date()) => {
  const d = new Date(base.getFullYear(), base.getMonth(), base.getDate() + n);
  return ymd(d);
};

// Parse a stored date value into a Date at LOCAL midnight.
// Date-only strings ('YYYY-MM-DD') — and legacy values that are a date followed by a
// zero UTC-midnight time ('...T00:00:00(.sss)Z') — are treated as the local calendar
// date they name, never shifted. All other timestamp values parse as normal instants.
export const toLocalDate = (value) => {
  if (!value) return null;
  if (typeof value === 'string') {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      const rest = value.slice(10);
      if (rest === '' || /^T00:00:00(\.\d+)?(Z|\+00:00)?$/.test(rest)) {
        return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      }
    }
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

// Display formatter for user-facing date columns: renders the LOCAL calendar date a
// value refers to (date-only and legacy UTC-midnight values included). '-' for empty.
export const fmtDate = (value) => {
  if (!value) return '-';
  const d = toLocalDate(value);
  return d ? d.toLocaleDateString() : '-';
};